import { useMemo, useState } from 'react'
import useSWR from 'swr'
import {
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ApexOptions } from 'apexcharts'

import { ApexChart } from 'ApexChart'
import { strings } from 'strings'
import {
  AutoStatsBase,
  AutoStatsDevice,
  useAutoStatsBases,
  useAutoStatsDevices,
} from 'api'

import {
  fetchJson,
  formatDayLabel,
  formatHourLabel,
  newAndTotalTooltip,
  toPersianDigits,
  useAnalyticsRange,
} from './analyticsShared'
import { UNKNOWN_PROVINCE, useProvinces } from './provinces'
import { BucketDetailsDialog } from './BucketDetailsDialog'

const EPOCH_ISO = '2000-01-01T00:00:00Z'
const ALL_PROVINCES = '__all__'

type Kind = 'bases' | 'devices'

interface GrowthBasePoint {
  date: string
  newCount: number
  cumulativeCount: number
  newIds?: number[]
}

interface GrowthDevicePoint {
  date: string
  newCount: number
  cumulativeCount: number
  newSerials?: string[]
}

interface GrowthResponse {
  bases?: GrowthBasePoint[]
  devices?: GrowthDevicePoint[]
}

async function fetchGrowth(
  endpoint: string,
  from: string,
  to: string,
  granularity: 'day' | 'hour',
): Promise<GrowthResponse> {
  return fetchJson<GrowthResponse>(endpoint, from, to, granularity)
}

interface DayBucket {
  dayKey: string
  ids: Array<number | string>
}

interface SeriesView {
  kind: Kind
  name: string
  color: string
  filteredByDay: DayBucket[]
  cumulative: number[]
  allAllowlistForList: Array<number | string> | null
}

const SERIES_DEFS: Array<{ kind: Kind; color: string; name: string }> = [
  { kind: 'devices', color: '#198D10', name: strings.devices },
  { kind: 'bases', color: '#1976d2', name: strings.bases },
]

export function ProvinceGrowthChart() {
  return <ProvinceGrowthCard />
}

function ProvinceGrowthCard() {
  const classes = useStyles()
  const { bucketSpec, allTimeToIso } = useAnalyticsRange()

  const [province, setProvince] = useState<string>(ALL_PROVINCES)
  const [selected, setSelected] = useState<{ day: string; kind: Kind } | null>(
    null,
  )
  const [listOpen, setListOpen] = useState<Kind | null>(null)

  const { data: growth, error: growthError } = useSWR<GrowthResponse>(
    ['/auto-stats/growth', bucketSpec.from, bucketSpec.to, bucketSpec.granularity],
    fetchGrowth,
  )

  // All-time entities so per-province cumulative starts from the proper
  // baseline (entities first-seen before the chart window).
  const basesResp = useAutoStatsBases(EPOCH_ISO, allTimeToIso)
  const devicesResp = useAutoStatsDevices(EPOCH_ISO, allTimeToIso)

  const { ready: provincesReady, provinceNames, resolve } = useProvinces()

  const error = growthError || basesResp.error || devicesResp.error
  const loading =
    !growth ||
    basesResp.loading ||
    devicesResp.loading ||
    !provincesReady

  const idMaps = useMemo(() => {
    if (!provincesReady) return null
    const baseMap = new Map<number | string, string>()
    if (basesResp.bases) {
      for (const b of basesResp.bases as AutoStatsBase[]) {
        const p = resolve(b.latitude, b.longitude)
        baseMap.set(b.mountpointId, p ?? UNKNOWN_PROVINCE)
      }
    }
    const deviceMap = new Map<number | string, string>()
    if (devicesResp.devices) {
      for (const d of devicesResp.devices as AutoStatsDevice[]) {
        const p = resolve(d.latitude, d.longitude)
        deviceMap.set(d.serial, p ?? UNKNOWN_PROVINCE)
      }
    }
    return { bases: baseMap, devices: deviceMap }
  }, [provincesReady, basesResp.bases, devicesResp.devices, resolve])

  const dayBucketsByKind = useMemo(() => {
    const make = (kind: Kind): DayBucket[] => {
      const raw = kind === 'bases' ? growth?.bases : growth?.devices
      const index = new Map<string, Array<number | string>>()
      if (raw) {
        for (const pt of raw) {
          const ids =
            kind === 'bases'
              ? ((pt as GrowthBasePoint).newIds ?? [])
              : ((pt as GrowthDevicePoint).newSerials ?? [])
          index.set(pt.date, ids)
        }
      }
      return bucketSpec.keys.map((k) => ({
        dayKey: k,
        ids: index.get(k) ?? [],
      }))
    }
    return { bases: make('bases'), devices: make('devices') }
  }, [growth, bucketSpec])

  const provincesInData = useMemo(() => {
    if (!idMaps) return []
    const present = new Set<string>()
    for (const kind of ['bases', 'devices'] as Kind[]) {
      const map = idMaps[kind]
      for (const day of dayBucketsByKind[kind]) {
        for (const id of day.ids) {
          present.add(map.get(id) ?? UNKNOWN_PROVINCE)
        }
      }
    }
    const allNames = [...provinceNames]
    if (present.has(UNKNOWN_PROVINCE)) allNames.push(UNKNOWN_PROVINCE)
    return allNames
  }, [idMaps, dayBucketsByKind, provinceNames])

  const seriesViews: SeriesView[] = useMemo(() => {
    return SERIES_DEFS.map(({ kind, color, name }) => {
      const map = idMaps?.[kind] ?? null
      const dayBuckets = dayBucketsByKind[kind]
      const filteredByDay: DayBucket[] = !map
        ? dayBuckets.map((d) => ({
            dayKey: d.dayKey,
            ids: [] as Array<number | string>,
          }))
        : dayBuckets.map((d) => {
            if (province === ALL_PROVINCES) return d
            return {
              dayKey: d.dayKey,
              ids: d.ids.filter(
                (id) => (map.get(id) ?? UNKNOWN_PROVINCE) === province,
              ),
            }
          })

      let totalInScope = 0
      if (map) {
        if (province === ALL_PROVINCES) totalInScope = map.size
        else {
          map.forEach((p) => {
            if (p === province) totalInScope++
          })
        }
      }

      const newPerDay = filteredByDay.map((d) => d.ids.length)
      const totalNewInWindow = newPerDay.reduce((a, b) => a + b, 0)
      const baseline = Math.max(0, totalInScope - totalNewInWindow)
      const cumulative: number[] = []
      let running = baseline
      for (const n of newPerDay) {
        running += n
        cumulative.push(running)
      }

      let allAllowlistForList: Array<number | string> | null = null
      if (map && province !== ALL_PROVINCES) {
        const out: Array<number | string> = []
        map.forEach((p, id) => {
          if (p === province) out.push(id)
        })
        allAllowlistForList = out
      }

      return {
        kind,
        name,
        color,
        filteredByDay,
        cumulative,
        allAllowlistForList,
      }
    })
  }, [idMaps, dayBucketsByKind, province])

  const isEmpty =
    !loading && seriesViews.every((s) => s.cumulative.every((v) => v === 0))

  const bucketAllowlist = useMemo(() => {
    if (!selected) return null
    const sv = seriesViews.find((s) => s.kind === selected.kind)
    if (!sv) return null
    const bucket = sv.filteredByDay.find((d) => d.dayKey === selected.day)
    return bucket ? bucket.ids : []
  }, [seriesViews, selected])

  const categories = useMemo(
    () =>
      bucketSpec.keys.map((k) =>
        bucketSpec.granularity === 'hour' ? formatHourLabel(k) : formatDayLabel(k),
      ),
    [bucketSpec],
  )

  const options: ApexOptions | null = useMemo(() => {
    if (loading || isEmpty) return null
    return buildOptions(categories, seriesViews, (kind, dayKey) =>
      setSelected({ day: dayKey, kind }),
    )
  }, [loading, isEmpty, categories, seriesViews])

  const provinceLabel =
    province === ALL_PROVINCES
      ? strings.allProvinces
      : province === UNKNOWN_PROVINCE
        ? strings.unknownProvince
        : province

  const dialogTitleBase = selected
    ? selected.kind === 'bases'
      ? strings.bases
      : strings.devices
    : ''

  const activeListView = listOpen
    ? (seriesViews.find((s) => s.kind === listOpen) ?? null)
    : null

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <Typography className={classes.title}>
          {strings.growthOfBasesAndDevices}
        </Typography>
        <div className={classes.controls}>
          <TextField
            select
            size="small"
            variant="outlined"
            value={province}
            onChange={(e) => setProvince(e.target.value as string)}
            className={classes.provinceSelect}
            SelectProps={{
              MenuProps: { PaperProps: { style: { maxHeight: 320 } } },
            }}
          >
            <MenuItem value={ALL_PROVINCES}>{strings.allProvinces}</MenuItem>
            {provincesInData.map((p) => (
              <MenuItem key={p} value={p}>
                {p === UNKNOWN_PROVINCE ? strings.unknownProvince : p}
              </MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setListOpen('devices')}
          >
            {strings.showAllDevices}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setListOpen('bases')}
          >
            {strings.showAllBases}
          </Button>
        </div>
      </div>
      <div className={classes.chartWrap}>
        {error && (
          <Typography className={classes.status}>
            {strings.error}: {String((error as any)?.message ?? error)}
          </Typography>
        )}
        {!error && loading && (
          <div className={classes.status}>
            <CircularProgress size={24} />
            <Typography>{strings.loading}</Typography>
          </div>
        )}
        {!error && !loading && isEmpty && (
          <Typography className={classes.status}>
            {strings.noGrowthInRange}
          </Typography>
        )}
        {!error && options && (
          <ApexChart options={options} className={classes.chart} />
        )}
      </div>
      <BucketDetailsDialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={
          selected
            ? `${dialogTitleBase} — ${
                bucketSpec.granularity === 'hour'
                  ? `${formatDayLabel(selected.day.slice(0, 10))} ${formatHourLabel(selected.day)}`
                  : formatDayLabel(selected.day)
              } — ${provinceLabel}`
            : ''
        }
        bucket={selected?.day ?? null}
        granularity={bucketSpec.granularity}
        kind={selected?.kind ?? 'devices'}
        mode="growth"
        baseIdAllowlist={
          selected?.kind === 'bases' && bucketAllowlist
            ? (bucketAllowlist as number[])
            : undefined
        }
        deviceSerialAllowlist={
          selected?.kind === 'devices' && bucketAllowlist
            ? (bucketAllowlist as string[])
            : undefined
        }
      />
      <BucketDetailsDialog
        open={listOpen !== null}
        onClose={() => setListOpen(null)}
        title={
          listOpen
            ? `${listOpen === 'bases' ? strings.allBases : strings.allDevices} — ${provinceLabel}`
            : ''
        }
        kind={listOpen ?? 'devices'}
        from={EPOCH_ISO}
        to={allTimeToIso}
        csvFilename={
          listOpen
            ? `${listOpen}-by-province-${province === ALL_PROVINCES ? 'all' : province}`
            : 'all'
        }
        baseIdAllowlist={
          listOpen === 'bases' && activeListView?.allAllowlistForList
            ? (activeListView.allAllowlistForList as number[])
            : undefined
        }
        deviceSerialAllowlist={
          listOpen === 'devices' && activeListView?.allAllowlistForList
            ? (activeListView.allAllowlistForList as string[])
            : undefined
        }
      />
    </div>
  )
}

function buildOptions(
  categories: string[],
  seriesViews: SeriesView[],
  onMarkerClick: (kind: Kind, dayKey: string) => void,
): ApexOptions {
  const discreteMarkers: Array<{
    seriesIndex: number
    dataPointIndex: number
    fillColor: string
    strokeColor: string
    size: number
  }> = []
  seriesViews.forEach((s, si) => {
    s.filteredByDay.forEach((d, pi) => {
      if (d.ids.length > 0) {
        discreteMarkers.push({
          seriesIndex: si,
          dataPointIndex: pi,
          fillColor: s.color,
          strokeColor: '#fff',
          size: 5,
        })
      }
    })
  })

  return {
    series: seriesViews.map((s) => ({
      name: s.name,
      data: s.cumulative,
    })),
    chart: {
      type: 'line',
      fontFamily: 'vazir',
      height: 360,
      animations: { enabled: false },
      toolbar: { show: true },
      events: {
        markerClick: (
          _e,
          _ctx,
          cfg: { seriesIndex?: number; dataPointIndex?: number },
        ) => {
          const si = cfg?.seriesIndex ?? 0
          const pi = cfg?.dataPointIndex ?? -1
          const sv = seriesViews[si]
          if (!sv || pi < 0) return
          const bucket = sv.filteredByDay[pi]
          if (bucket && bucket.ids.length > 0) {
            onMarkerClick(sv.kind, bucket.dayKey)
          }
        },
      },
    },
    stroke: { curve: 'straight', width: 3 },
    markers: { size: 0, discrete: discreteMarkers, hover: { size: 6 } },
    dataLabels: {
      // Label every series at points where the cumulative line steps up
      // (newCount > 0). Suppressing zero-delta points keeps labels from
      // repeating across long flat stretches.
      enabled: true,
      formatter: (_val, opts) => {
        const o = opts as { dataPointIndex?: number; seriesIndex?: number }
        const si = o.seriesIndex ?? 0
        const pi = o.dataPointIndex ?? -1
        const sv = seriesViews[si]
        if (!sv) return ''
        const newCount = sv.filteredByDay[pi]?.ids.length ?? 0
        if (newCount <= 0) return ''
        const cum = sv.cumulative[pi] ?? 0
        return toPersianDigits(cum)
      },
      offsetY: -8,
      style: {
        fontSize: '11px',
        colors: seriesViews.map((s) => s.color),
      },
      background: { enabled: false },
    },
    xaxis: {
      categories,
      labels: { rotate: -45, style: { fontSize: '11px' } },
    },
    yaxis: {
      min: 0,
      title: { text: strings.cumulativeCount },
      labels: {
        formatter: (val) => String(Math.round(val as number)),
      },
    },
    legend: { position: 'top', horizontalAlign: 'left' },
    colors: seriesViews.map((s) => s.color),
    tooltip: newAndTotalTooltip(
      seriesViews.map((s) => ({
        name: s.name,
        color: s.color,
        total: s.cumulative,
        newCount: s.filteredByDay.map((d) => d.ids.length),
      })),
    ),
  }
}

const useStyles = makeStyles((theme) => ({
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
    width: '100%',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    background: 'silver',
    flexWrap: 'wrap',
  },
  title: {
    fontWeight: 600,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  provinceSelect: {
    minWidth: 160,
    background: '#fff',
    '& .MuiOutlinedInput-inputMarginDense': {
      paddingTop: 6,
      paddingBottom: 6,
    },
  },
  chartWrap: {
    width: '100%',
    height: 360,
  },
  chart: {
    width: '100%',
    height: '100%',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    height: 360,
    justifyContent: 'center',
  },
}))
